import { useCallback, useEffect, useRef, useState } from "react";
import { trackProductEvent } from "../analytics/product";
import { useAuth } from "../auth/useAuth";
import { useNotificationPreferencesQuery } from "../hooks/useNotificationPreferencesQuery";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { toast } from "../utils/toast";
import {
  markPushNotificationPromptDismissed,
  PUSH_NOTIFICATION_PROMPT_EVENT,
  shouldShowPushNotificationPrompt,
  type PushNotificationPromptRequest,
} from "../utils/pushNotificationPrompt";
import "./PushNotificationPrompt.css";

const PROMPT_ANALYTICS = {
  pageKey: "app",
  routePath: "/",
  sourcePage: "PushNotificationPrompt",
  productArea: "notifications",
} as const;

export const PushNotificationPrompt = () => {
  const { isAuthenticated } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const pendingReasonRef = useRef<string | null>(null);
  const { data: preferences, isSuccess: preferencesLoaded } =
    useNotificationPreferencesQuery({
      enabled: isAuthenticated,
    });
  const {
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    refreshSubscriptionState,
  } = usePushNotifications();

  const canPrompt =
    isAuthenticated &&
    preferencesLoaded &&
    preferences?.push === true &&
    permission === "default" &&
    !isSubscribed &&
    shouldShowPushNotificationPrompt();

  const hidePrompt = useCallback(() => {
    setIsVisible(false);
    setReason(null);
  }, []);

  const showPrompt = useCallback(
    (nextReason: string) => {
      if (!canPrompt) {
        pendingReasonRef.current = nextReason;
        return;
      }

      pendingReasonRef.current = null;
      setReason(nextReason);
      setIsVisible(true);
      trackProductEvent("push_prompt_shown", PROMPT_ANALYTICS, {
        reason: nextReason,
      }, "notifications");
    },
    [canPrompt],
  );

  useEffect(() => {
    if (!preferencesLoaded || canPrompt) {
      return;
    }

    pendingReasonRef.current = null;
  }, [canPrompt, preferencesLoaded]);

  useEffect(() => {
    if (!canPrompt || !pendingReasonRef.current) {
      return;
    }

    const pendingReason = pendingReasonRef.current;
    showPrompt(pendingReason);
  }, [canPrompt, showPrompt]);

  useEffect(() => {
    const handlePromptRequest = (event: Event) => {
      const customEvent = event as CustomEvent<PushNotificationPromptRequest>;
      showPrompt(customEvent.detail?.reason ?? "unknown");
    };

    window.addEventListener(PUSH_NOTIFICATION_PROMPT_EVENT, handlePromptRequest);
    return () => {
      window.removeEventListener(
        PUSH_NOTIFICATION_PROMPT_EVENT,
        handlePromptRequest,
      );
    };
  }, [showPrompt]);

  useEffect(() => {
    if (!isVisible || canPrompt) {
      return;
    }

    hidePrompt();
  }, [canPrompt, hidePrompt, isVisible]);

  const handleDismiss = useCallback(() => {
    markPushNotificationPromptDismissed();
    trackProductEvent("push_prompt_dismissed", PROMPT_ANALYTICS, {
      reason: reason ?? "unknown",
    }, "notifications");
    hidePrompt();
  }, [hidePrompt, reason]);

  const handleEnable = useCallback(async () => {
    trackProductEvent("push_prompt_accepted", PROMPT_ANALYTICS, {
      reason: reason ?? "unknown",
    }, "notifications");

    const subscribed = await subscribe({
      analytics: PROMPT_ANALYTICS,
    });

    if (!subscribed) {
      toast.error(
        "Não foi possível ativar notificações push neste dispositivo.",
      );
      return;
    }

    if (!preferences?.push) {
      return;
    }

    await refreshSubscriptionState();
    hidePrompt();
    toast.success("Notificações push ativadas.");
  }, [
    hidePrompt,
    preferences?.push,
    reason,
    refreshSubscriptionState,
    subscribe,
  ]);

  if (!isVisible || permission === "unsupported") {
    return null;
  }

  return (
    <div
      className="push-notification-prompt"
      role="dialog"
      aria-live="polite"
      aria-label="Ativar notificações push"
    >
      <div className="push-notification-prompt__content">
        <p className="push-notification-prompt__title">
          Receba avisos importantes
        </p>
        <p className="push-notification-prompt__description">
          Ative as notificações para saber quando houver novos eventos,
          atividades e atualizações do seu terreiro.
        </p>
        <div className="push-notification-prompt__actions">
          <button
            type="button"
            className="push-notification-prompt__button push-notification-prompt__button--primary"
            onClick={() => {
              void handleEnable();
            }}
            disabled={isLoading}
          >
            Ativar notificações
          </button>
          <button
            type="button"
            className="push-notification-prompt__button push-notification-prompt__button--secondary"
            onClick={handleDismiss}
            disabled={isLoading}
          >
            Agora não
          </button>
        </div>
      </div>
      <button
        type="button"
        className="push-notification-prompt__close"
        aria-label="Fechar"
        onClick={handleDismiss}
      >
        ×
      </button>
    </div>
  );
};
